
import tsRest from 'ts-rest-client';
import { flatMap, map, isEmpty, isNumber } from 'lodash';
import { EMPTY_UUID } from '../src/logic/util';
import { ReferrerInfo, TrackerClientIds, TrackerClientIdsGoogleSessionId, TrackerEvent, TrackerEventAction } from './tracking-model';
import { TrackingEvent } from '../src/types/models';

const restCallTimeout = 4000;

export async function trackPageViewIsForIntegration(integ, location, pathname, isForIntegration) {
    const reffererInfo = await getClientSiteReferrerAndTitle(isForIntegration);
    trackPageView(integ, location, pathname, reffererInfo);
}


//#region Tracking logic
async function trackEvent(integ: string, virtualEvent: TrackerEvent): Promise<void> {
    if (virtualEvent == null || virtualEvent.type == null) return; //Guard

    // Tracking debugging
    // console.log("Tracking : ", JSON.stringify(virtualEvent, null, 4)); // Stringify because we want the whole object in the log.

    const trackerIds = retrieveTrackerIds();
    if (tsRest != undefined && integ != undefined && virtualEvent != undefined) {
        try {
            return await tsRest.post(`/integration/${integ}/trackers/newevent`, {
                trackerIds: trackerIds, userAgent: navigator.userAgent,
                event: virtualEvent
            }, { timeout: restCallTimeout });
        } catch (err) {
            console.log("Tracking timed out", err);
        }
    }
}
// TODO : Unify TrackerEvent | TrackingEvent into one type as soon as possible
export async function trackEcommerceEvent(integ: string, data: TrackerEvent | TrackingEvent): Promise<void> {
    const ecommerceEvent = await buildEcommerceEvent(data);
    await trackEvent(integ, ecommerceEvent);
}

export async function trackPageView(integ: string, hostname: string, pathname: string, referrerInfo: ReferrerInfo): Promise<void> {
    let actuelReferrerInfo = referrerInfo ? referrerInfo : await getClientSiteReferrerAndTitle(false);

    /** @type { import("./tracking-model").TrackerEvent} */
    let pageViewData = {
        type: 'pageview',
        hostname: hostname,
        path: pathname,
        referrer: actuelReferrerInfo.referrer,
        title: actuelReferrerInfo.title
    };

    if (isEmpty(pageViewData.referrer))
        pageViewData.referrer = hostname;

    trackEvent(integ, pageViewData);
}

/**
 */
export async function trackUserAction(integ: string, action: TrackerEventAction): Promise<void> {
    const reffererInfo = await getClientSiteReferrerAndTitle(false);

    /** @type { import("./tracking-model").TrackerEvent} */
    const userActionData = {
        type: 'userAction',
        action: action,
        referrer: reffererInfo.referrer,
        title: reffererInfo.title

    };
    await trackEvent(integ, userActionData);
}

//#endregion


//#region Utils
async function getClientSiteReferrerAndTitle(isInIntegration: boolean): Promise<ReferrerInfo> {
    const retInfo = {
        referrer: document.referrer,
        title: document.title
    };

    // If it is in the context of an integration, we go get the details
    // in the client's page instead because it is much more cooler.
    if(isInIntegration) {
        const bridgeResponse = await new Promise<ReferrerInfo>((resolve) => { 
            window.RCOMBRIDGE_getClientSiteReferrerAndTitle((response) => { resolve(response); }); 
        });

        if(!isEmpty(bridgeResponse.referrer))
            retInfo.referrer = bridgeResponse.referrer;

        if(!isEmpty(bridgeResponse.title))
            retInfo.title = bridgeResponse.title;
    } 
    
    if(isEmpty(retInfo.referrer))
        retInfo.referrer = document.location.href;

    return retInfo;
}


async function buildEcommerceEvent(eventData: TrackerEvent): Promise<TrackerEvent> {
    const reffererInfo = await getClientSiteReferrerAndTitle(false);

    let retEvent: TrackerEvent = {
        type: 'ecommerce',
        ecommerceAction: eventData.type,
        items: eventData.items,
        currency: eventData.currency,
        urlSource: window.location.href,
        referrer: reffererInfo.referrer,
        title: reffererInfo.title,
        transactionId: EMPTY_UUID,
        serviceFees: 0,
        shippingFees: 0,
        total: isNumber(eventData.total) ? eventData.total : 0
    };
    
    if (eventData.type === 'transaction') {
        retEvent.transactionId = eventData.transactionId;
        retEvent.serviceFees = eventData.serviceFees;
        retEvent.shippingFees = eventData.shippingFees;
        //retEvent.total = eventData.total;
    }

    return retEvent;
}

export function convertOrderItemsToTrackingItems(items) {
    return flatMap(items, item => {
        return map(item.Tickets, ticket => {
            return {
                name: item.Name,
                code: item.EventDateCode,
                category: item.EventCategoryFr, // Fix to french for reporting purposes
                producerName: item.ProducerName,
                price: ticket.Total,
                quantity: 1
            };
        });
    });
}

function getAllOrderItemsFromCart(cart) {
    return flatMap(cart.Items, item => {
        if (item.SubProducts != undefined && item.SubProducts.length > 0)
            return item.SubProducts;
        return item;
    });
}

export function prepareTransactionData(cart, cartID) {
    const orderItems = getAllOrderItemsFromCart(cart);
    return {
        type: 'transaction',
        items: convertOrderItemsToTrackingItems(orderItems),
        transactionId: cartID,
        serviceFees: cart.TotalServiceFees,
        shippingFees: cart.TotalShipingFees,
        total: cart.Total,
        currency: cart.CurrencyCode
    };
}
//#endregion

// Tools

function retrieveTrackerIds(): TrackerClientIds {
    const googleId = getClientIdForGA();

    return {
        googleId: googleId,
        googleAnalytics4Id: googleId,
        googleSessionIds: getGoogleSessionAndTrackerIds(), // Try catch around that!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
        facebookPixelId: getCookie('_fbp')
        //facebookClickId: getCookie('_fbc')
    };
}

function getCookie(c_name): string | null {
    let c_value = " " + document.cookie;
    let c_start = c_value.indexOf(" " + c_name + "=");
    if (c_start == -1) {
        c_value = null;
    }
    else {
        c_start = c_value.indexOf("=", c_start) + 1;
        let c_end = c_value.indexOf(";", c_start);
        if (c_end == -1) {
            c_end = c_value.length;
        }
        c_value = unescape(c_value.substring(c_start, c_end));
    }
    return c_value;
}

/**
 * This function is not perfect but it'll work for this case
 */
function getAllCookies(): { key: string, value: string }[] {
    const list = document.cookie.split(";");
    return list.reduce((acc, item) => {
        const keyVal = item.split("=");
        acc.push({
            key: keyVal[0].trim(),
            value: keyVal[1]
        });
        return acc;
    }, []);
}

function getClientIdForGA(): string | null {
    let cid = null;
    if(typeof(ga) != 'undefined'){
        ga(function(tracker) {
            cid = tracker.get('clientId');
         });
    }
    return cid;
}


const GOOGLE_SESSION_COOKIE_NAME_REGEX = /^_ga_(.*)/;
/**
 * Read all google session cookies and build an array with that. We
 * send them all to the server.
 */
function getGoogleSessionAndTrackerIds(): TrackerClientIdsGoogleSessionId[] {
    try {
        const cookies = getAllCookies();

        // Search the session cookies. They are starting with "_ga_";
        return cookies.map(cookie => {
            const matchRes = GOOGLE_SESSION_COOKIE_NAME_REGEX.exec(cookie.key);
            if(matchRes == null) return null;
            
            return {
                trackerId: 'G-' + matchRes[1],
                sessionId: parseGoogleSessionIdFromSessionCookie(cookie.value)
            };
        }).filter(cookie => cookie != null);
        
    } catch(err) {
        console.log('Unable to parse ga session', err);
        return [];
    }
}

/**
 * Receives a thing like 'GS1.1.1675284993.8.1.1675286537.0.0.0' and return '1675284993'
 */
function parseGoogleSessionIdFromSessionCookie(sessionCookie: string): string {
    const sessionCookieParts = sessionCookie.split('.');
    if(sessionCookieParts.length < 3) return null;
    return sessionCookieParts[2];
}